import { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { FileRow } from "@shared/types";
import { humanSize, formatDateShort } from "@/lib/format";
import { Avatar } from "./Avatar";
import { FileThumb } from "./FileThumb";
import { Skeleton } from "./Skeleton";

type SortKey = "name" | "size" | "uploaded_at" | "project" | "uploader";
type Dir = "asc" | "desc";

// The Files toolbar chips are finer-grained (pdf/doc/xls/img/other) than the
// server `type` filter (image/doc/other); collapse them. pdf+xls both fall
// under "doc" server-side — a known limitation of the list view's filter.
function chipToServerType(
  filter: string,
): "image" | "doc" | "other" | undefined {
  if (filter === "img") return "image";
  if (filter === "pdf" || filter === "doc" || filter === "xls") return "doc";
  if (filter === "other") return "other";
  return undefined;
}

function extOf(filename: string): string {
  return (filename.split(".").pop() ?? "").toLowerCase();
}

const col = createColumnHelper<FileRow>();

export function FilesTable({
  search,
  filter,
}: {
  search: string;
  filter: string;
}) {
  const [sort, setSort] = useState<SortKey>("uploaded_at");
  const [dir, setDir] = useState<Dir>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const type = chipToServerType(filter);
  const q = search.trim() || undefined;

  // Any change to what we're querying invalidates the current page number.
  useEffect(() => setPage(1), [q, type, sort, dir, pageSize]);

  const query = useQuery({
    queryKey: queryKeys.files({ q, type, sort, dir, page, pageSize }),
    queryFn: () => api.listFiles({ q, type, sort, dir, page, pageSize }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.files ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Clicking a sortable header toggles direction, or switches to that column
  // (defaulting to descending, matching the date-first default).
  function toggleSort(key: SortKey) {
    if (sort === key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDir(
        key === "name" || key === "project" || key === "uploader"
          ? "asc"
          : "desc",
      );
    }
  }

  const columns = useMemo(
    () => [
      col.display({
        id: "thumb",
        header: "",
        cell: (ctx) => (
          <FileThumb
            id={ctx.row.original.id}
            ext={extOf(ctx.row.original.filename)}
          />
        ),
      }),
      col.accessor("filename", {
        id: "name",
        header: () => <SortHeader label="Name" k="name" />,
        cell: (ctx) => (
          <a
            className="ft-name"
            href={`/api/attachments/${ctx.row.original.id}/raw`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ctx.getValue()}
          </a>
        ),
      }),
      col.accessor("project_name", {
        id: "project",
        header: () => <SortHeader label="Project" k="project" />,
        cell: (ctx) => <span className="t-muted">{ctx.getValue()}</span>,
      }),
      col.accessor("task_title", {
        id: "task",
        header: "Related task",
        cell: (ctx) => {
          const r = ctx.row.original;
          if (!r.task_title) return <span className="t-muted">—</span>;
          return r.related_task_id ? (
            <Link
              to="/tasks/$taskId"
              params={{ taskId: r.related_task_id }}
              className="ft-task"
            >
              {r.task_title}
            </Link>
          ) : (
            <span>{r.task_title}</span>
          );
        },
      }),
      col.accessor("uploader_name", {
        id: "uploader",
        header: () => <SortHeader label="Uploaded by" k="uploader" />,
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className="ft-uploader">
              <Avatar
                id={r.uploaded_by}
                name={r.uploader_name ?? r.uploaded_by}
                size={20}
                image={r.uploader_image}
              />
              <span>{r.uploader_name ?? "Unknown"}</span>
            </span>
          );
        },
      }),
      col.accessor("uploaded_at", {
        id: "date",
        header: () => <SortHeader label="Date" k="uploaded_at" />,
        cell: (ctx) => (
          <span className="t-muted">{formatDateShort(ctx.getValue())}</span>
        ),
      }),
      col.accessor("size_bytes", {
        id: "size",
        header: () => <SortHeader label="Size" k="size" />,
        cell: (ctx) => (
          <span className="t-muted">{humanSize(ctx.getValue() ?? 0)}</span>
        ),
      }),
    ],
    // SortHeader closes over sort/dir/toggleSort; rebuild when they change.
    [sort, dir],
  );

  function SortHeader({ label, k }: { label: string; k: SortKey }) {
    const active = sort === k;
    return (
      <button
        type="button"
        className={`ft-sort${active ? " active" : ""}`}
        onClick={() => toggleSort(k)}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className="ft-caret" aria-hidden="true">
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    );
  }

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    pageCount,
  });

  if (query.isLoading) {
    return <Skeleton count={8} height={44} />;
  }

  if (total === 0) {
    return (
      <p className="t-sm t-muted" style={{ padding: "24px 4px" }}>
        No files match.
      </p>
    );
  }

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="files-table-wrap">
      <table className="files-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id} className={`ft-th ft-th-${h.column.id}`}>
                  {h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="ft-row">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={`ft-td ft-td-${cell.column.id}`}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ft-pager">
        <span className="t-sm t-muted">
          {first}–{last} of {total}
        </span>
        <div className="ft-pager-controls">
          <label className="ft-pagesize t-sm t-muted">
            Rows
            <select
              className="select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ width: 72 }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          <span className="t-sm">
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
